import { AiCallAgentEnum, AiCallForCallPopDTO, CallOutcomeEnum, Company, TradeEnum } from '_lib/api/apiTypes.shim';

export const MAGIC_AI_CALL_ID_DEMO_NUMBERS = {
  'a1234-5678': CallOutcomeEnum.NewAppointment,
  'b1234-5678': CallOutcomeEnum.UpdateNotes,
  'c1234-5678': CallOutcomeEnum.Reschedule,
  'd1234-5678': CallOutcomeEnum.Cancellation,
  'e1234-5678': CallOutcomeEnum.WrongNumber
} as const;
export const MAGIC_AI_CALL_ID_DEMO_NUMBERS_KEYS = Object.keys(MAGIC_AI_CALL_ID_DEMO_NUMBERS);

export const isMagicAiCallIdDemoNumber = (aiCallId: string): aiCallId is keyof typeof MAGIC_AI_CALL_ID_DEMO_NUMBERS => {
  return MAGIC_AI_CALL_ID_DEMO_NUMBERS_KEYS.includes(aiCallId);
};

const getMockDataFromOutcome = ({
  outcome,
  company,
  aiCallId,
  companyDNIS,
  callerId
}: {
  outcome:
    | CallOutcomeEnum.NewAppointment
    | CallOutcomeEnum.UpdateNotes
    | CallOutcomeEnum.Reschedule
    | CallOutcomeEnum.Cancellation
    | CallOutcomeEnum.WrongNumber;
  company: Company;
  aiCallId: string;
  companyDNIS: string;
  callerId: string;
}): AiCallForCallPopDTO['aiCall'] => {
  const baseTime = new Date();
  const createTranscriptTime = (secondsOffset: number) =>
    new Date(baseTime.getTime() + secondsOffset * 1000).toISOString();

  const companyName = company.name;
  const primaryTrade = company.callCenter.trades?.[0] || 'HVAC';

  // Base object with all required properties
  const baseAiCall: AiCallForCallPopDTO['aiCall'] = {
    id: aiCallId,
    isActive: true,
    type: 'Inbound',
    agent: AiCallAgentEnum.CpiAgent,
    createdById: company.modifiedById,
    modifiedById: company.modifiedById,
    callCenterId: company.callCenterId,
    accountId: company.accountId,
    companyId: company.id,
    phoneCallId: null,
    callerId,
    telephonyId: null,
    outcome,
    companyDNIS,
    companySwitched: false,
    originalCompany: null,
    originalCompanyId: null,
    caller: null,
    quickSummary: null,
    summary: null,
    completionStatus: 'transfer-to-agent',
    sipTransferIntent: null,
    transcriptMessages: [],
    providerCall: {
      providerId: `demo-provider-${aiCallId}`,
      providerType: 'vapi',
      endedReason: null
    },
    createdAt: baseTime.toISOString(),
    modifiedAt: baseTime.toISOString(),
    startedAt: baseTime.toISOString(),
    endedAt: new Date(baseTime.getTime() + 45000).toISOString()
  };

  // Helper to get trade-specific service descriptions
  const getTradeService = (trade: TradeEnum): { service: string; issue: string; equipment: string } => {
    if (trade === TradeEnum.Plumbing) {
      return { service: 'water heater installation', issue: 'leaking', equipment: 'water heater' };
    } else if (trade === TradeEnum.Electric) {
      return { service: 'electrical panel upgrade', issue: 'tripping breakers', equipment: 'electrical panel' };
    } else if (trade === TradeEnum.HVAC) {
      return { service: 'HVAC system repair', issue: 'not cooling properly', equipment: 'AC unit' };
    } else {
      return { service: 'service appointment', issue: 'needs attention', equipment: 'system' };
    }
  };

  const tradeInfo = getTradeService(primaryTrade);

  switch (outcome) {
    case CallOutcomeEnum.NewAppointment:
      return {
        ...baseAiCall,
        quickSummary: `Customer Sarah Johnson called to schedule a ${tradeInfo.service}. She mentioned her current ${tradeInfo.equipment} is ${tradeInfo.issue} and needs immediate replacement. She has availability tomorrow at 2 PM and is being transferred to a live agent to schedule the appointment.`,
        companySwitched: false,
        caller: {
          name: 'Sarah Johnson',
          phoneNumber: '5551234567'
        },
        transcriptMessages: [
          {
            role: 'bot',
            message: `Thank you for calling ${companyName}! How can I help you today?`,
            time: createTranscriptTime(0)
          },
          {
            role: 'user',
            message: `Hi, my ${tradeInfo.equipment} is ${tradeInfo.issue} and I need to get it replaced as soon as possible.`,
            time: createTranscriptTime(3)
          },
          {
            role: 'bot',
            message:
              'I understand how urgent that is. Let me connect you with one of our scheduling specialists who can help you book an appointment right away. Can I get your name and phone number first?',
            time: createTranscriptTime(8)
          },
          {
            role: 'user',
            message: "Sure, it's Sarah Johnson and my number is 555-123-4567.",
            time: createTranscriptTime(12)
          },
          {
            role: 'user',
            message: 'I have availability tomorrow at 2 PM if that works.',
            time: createTranscriptTime(15)
          },
          {
            role: 'bot',
            message: `Perfect, Sarah! I'm transferring you now to a live agent from ${companyName} who will get that ${tradeInfo.service} scheduled for you. Please hold for just a moment.`,
            time: createTranscriptTime(18)
          },
          {
            role: 'user',
            message: 'Great, thank you so much!',
            time: createTranscriptTime(22)
          }
        ]
      };

    case CallOutcomeEnum.UpdateNotes:
      return {
        ...baseAiCall,
        quickSummary: `Customer Mike Davis called to provide additional information about his upcoming ${tradeInfo.service} appointment scheduled for Thursday at 10 AM. He mentioned the ${tradeInfo.equipment} is making grinding noises and wants to ensure the technician brings replacement parts. Being transferred to a live agent to add these notes to his appointment.`,
        companySwitched: false,
        caller: {
          name: 'Mike Davis',
          phoneNumber: '5559876543'
        },
        transcriptMessages: [
          {
            role: 'bot',
            message: `Thank you for calling ${companyName}! How may I assist you today?`,
            time: createTranscriptTime(0)
          },
          {
            role: 'user',
            message: `Hi, I have an appointment scheduled for Thursday, and I wanted to add some information about what's going on with my ${tradeInfo.equipment}.`,
            time: createTranscriptTime(2)
          },
          {
            role: 'bot',
            message:
              'Of course! I can help you with that. Can I get your name and phone number to look up your appointment?',
            time: createTranscriptTime(8)
          },
          {
            role: 'user',
            message: "It's Mike Davis, 555-987-6543.",
            time: createTranscriptTime(11)
          },
          {
            role: 'bot',
            message: 'Thank you, Mike. What additional information would you like to add to your appointment?',
            time: createTranscriptTime(14)
          },
          {
            role: 'user',
            message: `The ${tradeInfo.equipment} has been making a grinding noise, especially when it first turns on. I'm worried it might need some parts replaced. Could the technician bring replacements just in case?`,
            time: createTranscriptTime(18)
          },
          {
            role: 'bot',
            message:
              "That's important information for the technician to know. Let me transfer you to one of our scheduling specialists who can add these notes to your Thursday appointment and make sure the technician is prepared. Please hold for a moment.",
            time: createTranscriptTime(25)
          },
          {
            role: 'user',
            message: 'Perfect, thanks for your help!',
            time: createTranscriptTime(32)
          }
        ]
      };

    case CallOutcomeEnum.Reschedule:
      return {
        ...baseAiCall,
        quickSummary: `Customer Jennifer Martinez called to reschedule her ${tradeInfo.service} appointment from Friday at 9 AM due to a work conflict. She requested Monday at 3 PM instead. Being transferred to a live agent to complete the rescheduling.`,
        companySwitched: false,
        caller: {
          name: 'Jennifer Martinez',
          phoneNumber: '5552468135'
        },
        transcriptMessages: [
          {
            role: 'bot',
            message: `Hello! Thank you for calling ${companyName}. How can I help you?`,
            time: createTranscriptTime(0)
          },
          {
            role: 'user',
            message:
              'Hi, I need to reschedule my appointment for Friday. Something came up at work and I can no longer make it.',
            time: createTranscriptTime(3)
          },
          {
            role: 'bot',
            message: 'I understand. Let me help you with that. Can I get your name and phone number?',
            time: createTranscriptTime(8)
          },
          {
            role: 'user',
            message: 'Jennifer Martinez, 555-246-8135.',
            time: createTranscriptTime(11)
          },
          {
            role: 'bot',
            message: `Thank you, Jennifer. I can see you have a ${tradeInfo.service} appointment scheduled. When would work better for you?`,
            time: createTranscriptTime(14)
          },
          {
            role: 'user',
            message: 'Do you have anything available Monday afternoon?',
            time: createTranscriptTime(19)
          },
          {
            role: 'bot',
            message:
              'Let me transfer you to one of our scheduling specialists who can check availability and reschedule your appointment for Monday afternoon. Please hold for just a moment.',
            time: createTranscriptTime(21)
          },
          {
            role: 'user',
            message: 'Perfect! Thank you!',
            time: createTranscriptTime(27)
          }
        ]
      };

    case CallOutcomeEnum.Cancellation:
      return {
        ...baseAiCall,
        quickSummary: `Customer Robert Thompson called to cancel his ${tradeInfo.service} appointment scheduled for Wednesday. He found another service provider who could handle an emergency repair sooner. Being transferred to a live agent to process the cancellation.`,
        companySwitched: false,
        caller: {
          name: 'Robert Thompson',
          phoneNumber: '5558642097'
        },
        transcriptMessages: [
          {
            role: 'bot',
            message: `Good afternoon! Thank you for calling ${companyName}. How can I assist you today?`,
            time: createTranscriptTime(0)
          },
          {
            role: 'user',
            message: "Hi, I need to cancel my appointment. I'm really sorry about the short notice.",
            time: createTranscriptTime(2)
          },
          {
            role: 'bot',
            message: 'No worries at all. Can I get your name and phone number to pull up your appointment?',
            time: createTranscriptTime(6)
          },
          {
            role: 'user',
            message: 'Robert Thompson, 555-864-2097.',
            time: createTranscriptTime(9)
          },
          {
            role: 'bot',
            message: `Thank you, Robert. I see your ${tradeInfo.service} appointment for Wednesday. May I ask the reason for cancellation?`,
            time: createTranscriptTime(12)
          },
          {
            role: 'user',
            message: `I had an emergency with my ${tradeInfo.equipment} and needed someone today. I found another provider who could come right away, so I won't need the Wednesday appointment anymore.`,
            time: createTranscriptTime(16)
          },
          {
            role: 'bot',
            message:
              'I completely understand - emergencies happen. I hope everything is getting taken care of. Let me transfer you to one of our scheduling specialists who can cancel your Wednesday appointment. One moment please.',
            time: createTranscriptTime(23)
          },
          {
            role: 'user',
            message: 'I appreciate your understanding. Thanks for your help!',
            time: createTranscriptTime(30)
          }
        ]
      };

    case CallOutcomeEnum.WrongNumber:
      return {
        ...baseAiCall,
        quickSummary: `Caller reached out looking for a sister company with different services. After checking, they were transferred to the correct company. The caller was trying to schedule service for their ${tradeInfo.equipment}.`,
        companySwitched: true,
        originalCompany: 'Sister Company Inc.',
        originalCompanyId: 'not-a-real-company-id',
        caller: {
          name: 'Lisa Anderson',
          phoneNumber: '5557891234'
        },
        transcriptMessages: [
          {
            role: 'bot',
            message: `Thank you for calling Sister Company Inc.! How can I help you today?`,
            time: createTranscriptTime(0)
          },
          {
            role: 'user',
            message: `Hi, I need to schedule service for my ${tradeInfo.equipment}. It's ${tradeInfo.issue} and needs attention.`,
            time: createTranscriptTime(3)
          },
          {
            role: 'bot',
            message: `I see. Just to confirm, you're calling about ${tradeInfo.service}, is that correct? Let me verify we're the right company for your needs.`,
            time: createTranscriptTime(7)
          },
          {
            role: 'user',
            message:
              "Yes, that's right. I thought this was the number I was given. Do you handle this type of service?",
            time: createTranscriptTime(13)
          },
          {
            role: 'bot',
            message: `Actually, we have a sister company, ${companyName} that specializes in this service! Let me transfer you to them right now and they can help you with your issue. One moment please.`,
            time: createTranscriptTime(18)
          },
          {
            role: 'user',
            message: 'Oh perfect! That would be great, thank you so much!',
            time: createTranscriptTime(24)
          },
          {
            role: 'bot',
            message: "My pleasure! Transferring you now to our sister company. They'll take great care of you!",
            time: createTranscriptTime(26)
          }
        ]
      };

    default:
      return baseAiCall;
  }
};

export const mockAiCallForCallPopDTO = ({
  company,
  companyDNIS,
  aiCallId,
  callerId
}: {
  company: Company;
  companyDNIS: string;
  aiCallId: keyof typeof MAGIC_AI_CALL_ID_DEMO_NUMBERS;
  callerId?: string;
}): AiCallForCallPopDTO['aiCall'] => {
  const outcome = MAGIC_AI_CALL_ID_DEMO_NUMBERS[aiCallId];
  const mockData = getMockDataFromOutcome({
    outcome,
    company,
    aiCallId,
    companyDNIS,
    callerId: callerId || '5555555555'
  });

  return mockData;
};
